
import { defineComponent, ref } from 'vue';
import NotifyWarning from './notifications/NotifyWarning.vue';
import StdNotifications from '@/components/StdNotifications.vue';
import NotifySuccess from '@/components/notifications/NotifySuccess.vue';
import LoadingIcon from '@/components/LoadingIcon.vue';
import ExplorerLink from '@/components/ExplorerLink.vue';
import ModalWindow from '@/components/ModalWindow.vue';
import ContentTooltipMetadata from '@/components/content/tooltip/ContentTooltipMetadata.vue';
import useWallet from '@/composables/wallet';
import useError from '@/composables/error';
import useModal from '@/composables/modal';
import QuestionMark from '@/components/QuestionMark.vue';
import { NFTSignMetadata } from '@/common/NFTsign';
import useCluster from '@/composables/cluster';
import { pause } from '@/common/helpers/util';
import ContentTooltipNftToken from '@/components/content/tooltip/ContentTooltipNftToken.vue';
import { NFTCreateMaster, NFTCreateMetadata } from '@/common/NFTcreate';
import { DEFAULTS } from '@/globals';

export default defineComponent({
  components: {
    ContentTooltipNftToken,
    QuestionMark,
    ContentTooltipMetadata,
    ModalWindow,
    ExplorerLink,
    LoadingIcon,
    NotifySuccess,
    StdNotifications,
    NotifyWarning,
  },
  setup() {
    const { isConnected, getWallet } = useWallet();
    const { error, clearError, setError, tryConvertToPk, tryParseJSON, tryParseMetadataData } =
      useError();
    const { getConnection } = useCluster();

    const chosenAction = ref<string>('signMetadata');
    const isLoading = ref<boolean>(false);
    const txId = ref<string | null>(null);
    const pda = ref<string | null>(null);
    const confirmed = ref<boolean>(false);

    const clearPreviousResults = () => {
      isLoading.value = false;
      txId.value = null;
      pda.value = null;
      confirmed.value = false;
      clearError();
    };

    const checkConfirmed = async (sig: string) => {
      const conn = getConnection('confirmed');
      const tx = await conn.getConfirmedTransaction(sig);
      if (!tx) {
        await pause(1000);
        await checkConfirmed(sig);
        return;
      }
      confirmed.value = true;
    };

    // --------------------------------------- sign metadata
    const editionMint = ref<string | null>(null);

    const signMetadata = async () => {
      clearPreviousResults();
      isLoading.value = true;

      const editionPk = tryConvertToPk(editionMint.value);
      if (error.value) {
        return;
      }

      NFTSignMetadata(getWallet() as any, editionPk!)
        .then(async (result: string) => {
          txId.value = result;
          isLoading.value = false;
          await checkConfirmed(txId.value!);
        })
        .catch((e) => {
          setError(e);
          isLoading.value = false;
        });
    };

    // --------------------------------------- create metadata
    const newMetadataData = ref<any>(null);
    const newUpdateAuthority = ref<string | null>(null);

    const createMetadata = async () => {
      clearPreviousResults();
      isLoading.value = true;

      const parsedJSON = tryParseJSON(newMetadataData.value);
      let parsedMetadata;
      if (parsedJSON) parsedMetadata = tryParseMetadataData(parsedJSON);
      const editionPk = tryConvertToPk(editionMint.value);
      const updatePk = tryConvertToPk(newUpdateAuthority.value);
      if (error.value) {
        return;
      }

      NFTCreateMetadata(
        getWallet() as any,
        editionPk!,
        parsedMetadata as any, // null-undefined conflict
        updatePk as any // null-undefined conflict
      )
        .then(async (result: any) => {
          txId.value = result.txId;
          pda.value = result.metadata;
          isLoading.value = false;
          await checkConfirmed(txId.value!);
        })
        .catch((e) => {
          setError(e);
          isLoading.value = false;
        });
    };

    // --------------------------------------- create master
    const maxSupply = ref<number | null>(null);

    const createMaster = async () => {
      clearPreviousResults();
      isLoading.value = true;

      const editionPk = tryConvertToPk(editionMint.value);
      const updatePk = tryConvertToPk(newUpdateAuthority.value);
      if (error.value) {
        return;
      }

      NFTCreateMaster(
        getWallet() as any,
        editionPk!,
        updatePk as any, // null-undefined conflict
        maxSupply.value as any // null-undefined conflict
      )
        .then(async (result: any) => {
          txId.value = result.txId;
          pda.value = result.edition;
          isLoading.value = false;
          await checkConfirmed(txId.value!);
        })
        .catch((e) => {
          setError(e);
          isLoading.value = false;
        });
    };

    // --------------------------------------- modals
    const { registerModal, isModalVisible, showModal, hideModal } = useModal();
    registerModal('tooltipMetadata');
    registerModal('nftToken');

    return {
      DEFAULTS,
      isConnected,
      error,
      chosenAction,
      isLoading,
      txId,
      pda,
      confirmed,
      // sign
      editionMint,
      signMetadata,
      // create metadata
      newMetadataData,
      newUpdateAuthority,
      createMetadata,
      // create master
      maxSupply,
      createMaster,
      // modal
      isModalVisible,
      showModal,
      hideModal,
    };
  },
});
